// eslint-disable-next-line simple-import-sort/imports
import {
  ArrowDownTrayIcon,
  ArrowTrendingUpIcon,
  CheckIcon,
  ShieldCheckIcon,
  Square2StackIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType, setBorrowerGroupData, setLoanDetail } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { getEffectiveFieldRequiredForBorrower } from 'config/borrowerFieldRequired.util'
import { FieldOrigin, ILoanOriginSection, InputType, IVisibleProp } from 'config'
import { useLockContext } from 'context/LockContext'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { store } from 'reducers'
import { LoanType } from 'reducers/loanDetail.reducer'
import {
  borrowerInfoUpdate,
  copyApplicationToUrl,
  createLoanLiabilityInformation,
  createRentalEstateInformation,
  downloadFile,
  getAssociatedPartyUpdates,
  getBorrowerSignStatus,
  getSSAPdf,
  sendApplicationTo,
  sendApplicationToCoBorrowers,
  senDrawRequestTo,
  updateLoanFields,
  updateLoanProcess,
  verifyBorrowerTin,
} from 'services'
import { Button } from 'stories/components'
import { svgLoading } from 'stories/assets'
import {
  canEditLoanApplicationForBorrower,
  capitalizeFirstLetter,
  confirm,
  confirmWithCheckboxes,
  entityTaxIDConvertor,
  getLoanDisplayNo,
  InputConvert,
  InputValidate,
  isBorrowerUrl,
  openAuditLog,
  openInputsFormDlg,
  openInputsTableDlg,
  trimObjectStrings,
} from 'utils'
import { copyClipboard } from 'utils/copyClipboard'
import { isReadOnlyByEditPermission, isViewOnlyByLogic } from 'utils/loan'
import { RenderInput } from 'utils/RenderInput'
import { usePermissions } from 'hooks/usePermissions'

import { AlternativeNamesModal } from './AlternativeNamesModal'
import { BorrowerAiExtractModal, BorrowerAiField } from './BorrowerAiExtractModal'
import { defaultInputs, ResidenceInputs } from './constants'
import { companionsDrawnInSections, withCompanions } from './fieldCompanions'
import { borrowerInfoValidate, visibleBorrowerFieldsLogic } from './logic'
import { isConstructionLoan } from 'utils/isConstructionLoan'
import { TinVerifyModal } from 'utils/modals/tin-verify-history'
import { BorrowerSeperator } from '../config'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { setBorrowerSeparator } from 'utils/setLoanNumber'
import { signAppValidate } from '../SignApplication/logic'
import { IResidence, ResidenceType } from './types'
import { AdditionalLayerInputs } from './AdditionalLayerInputs'
import { reviewOutdatedConditions } from './outdated-condition-review'
import {
  AssociatedPartyMatch,
  formatAssociatedPartyLabel,
  hasSharedPartyIdentityChanges,
} from '../sharedPartySync.util'
// import { LinkedEntitySelect } from './LinkedEntitySelect'

enum ValidateStatus {
  Failed = -2,
  Warning = -1,
  Nothing = 0,
  Loading = 1,
  Success = 2,
}

export function BorrowerInformation(props: {
  borrowerSeperator: BorrowerSeperator
  setLoading: (loading: boolean) => void
  aiExtractOpen?: boolean
  onAiExtractClose?: () => void
}) {
  const { borrowerSeperator } = props
  const [action, setAction] = useState('')
  const [inputs, setInputs] = useState(defaultInputs())
  const [fieldValueChanged, setFieldValueChanged] = useState(false)
  // Input2 date blur calls onChange then onBlur in the same turn (keyboard path). React state
  // from setFieldValueChanged(true) has not re-rendered yet, so onBlur must read a ref or it
  // returns early and never PUT /loan/borrowerInfo — DOB looks filled until reload.
  const fieldValueChangedRef = useRef(false)
  const [visibelFields, setVisibleFields] = useState(visibleBorrowerFieldsLogic(borrowerSeperator))
  const [isAlternativeNames, showAlternativeNames] = useState(false)
  const [showTinHistory, setShowTinHistory] = useState(false)

  const [tinValidateStatus, setTinValidateStatus] = useState<ValidateStatus>(ValidateStatus.Nothing)
  const [needVerifyTin, setNeedVerifyTin] = useState(true)
  const location = useLocation()

  const { pathname } = location
  const isSignApplicationLink = useMemo(() => isBorrowerUrl(pathname), [location])

  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()

  const canTinCheck = hasPermission('Loan.Application.Borrower.TinCheck')

  const [loan, _borrower, loanDetail, fieldProperties, vendor, profile] = useSelector((state: any) => {
    return [
      state.loan,
      state.borrower,
      state.loanDetail,
      ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.BorrowerInformation] || {}) as Record<
        string,
        IVisibleProp
      >,
      state.vendor,
      state.auth.profile,
    ]
  })

  const saveBorrowerInfoWithConditionPrompt = async (data: Record<string, any>) => {
    let targetLoanNumbers: number[] = []
    if (hasSharedPartyIdentityChanges(data) && !profile?.isBorrower) {
      try {
        const preview = await getAssociatedPartyUpdates(data.borrowerSeperator)
        const matches: AssociatedPartyMatch[] = preview?.matches || []
        if (matches.length) {
          props.setLoading(false)
          const confirmed = await confirmWithCheckboxes(
            'Apply this shared entity/guarantor update to associated clone group files?',
            matches.map((match) => ({
              key: String(match.loanNumber),
              label: formatAssociatedPartyLabel(match),
              defaultChecked: true,
            })),
            { titleYes: 'Apply', titleNo: 'This file only' },
          )
          props.setLoading(true)
          if (confirmed) {
            targetLoanNumbers = matches
              .filter((match) => confirmed[String(match.loanNumber)])
              .map((match) => match.loanNumber)
          }
        }
      } catch (_e) {
        // Preview failures should not block saving the current file.
      }
    }

    const response = await borrowerInfoUpdate(data, true, {
      applyToAssociated: targetLoanNumbers.length > 0,
      targetLoanNumbers,
    })
    if (targetLoanNumbers.length && response?.associatedApply?.applied?.length) {
      toast(
        `Updated ${response.associatedApply.applied.length} associated file(s): ${response.associatedApply.applied
          .map((item: AssociatedPartyMatch) => item.loanNumber)
          .join(', ')}`,
        { type: 'info' },
      )
    }
    await reviewOutdatedConditions(response, props.setLoading)
    return response
  }

  const borrower = useMemo(() => {
    const temp = cloneDeep(_borrower)
    Object.keys(temp).forEach((key) => {
      if (key !== 'borrower' && !temp[key].isIncluded) delete temp[key]
    })
    return temp
  }, [_borrower])

  const isSelfBorrower = !profile.isBorrower || borrower[borrowerSeperator]?.email === profile.email

  const { loanFieldSections } = useSelector((state: any) => ({
    loanFieldSections: (((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})['sections'] || {})[
      FieldOrigin.BorrowerInformation
    ] || []) as ILoanOriginSection[],
  }))

  const freeSectionFields = useMemo(() => {
    if (!inputs) return []
    const keys = Object.keys(inputs).filter((key) => {
      return !loanFieldSections.find((section) => section.fields.includes(key))
    })
    // A host that sits in a section draws its own companions there, so they must not also appear
    // down here.
    const drawnInSections = companionsDrawnInSections(loanFieldSections)
    return withCompanions(keys.filter((key) => !drawnInSections.includes(key)))
  }, [loanFieldSections, inputs])

  useEffect(() => {
    const hasEntityTitleProp = fieldProperties?.['hasEntityTitle']
    const effectiveHasEntityTitle =
      hasEntityTitleProp?.visible === false ? !!borrower.borrower?.entityTitle : borrower.borrower?.hasEntityTitle
    let entityTitle = borrower.borrower?.entityTitle
    if (effectiveHasEntityTitle !== true) entityTitle = ''
    dispatch(
      setLoanDetail({
        entityTitle,
      }),
    )
  }, [borrower.borrower?.hasEntityTitle, borrower.borrower?.entityTitle, fieldProperties])

  useEffect(() => {
    setVisibleFields(visibleBorrowerFieldsLogic(borrowerSeperator))
  }, [borrower, loanDetail.includeCoborrower])

  useEffect(() => {
    let _visibelFields = visibleBorrowerFieldsLogic(borrowerSeperator)
    setVisibleFields(_visibelFields)
  }, [borrowerSeperator])

  useEffect(() => {
    setBorrowerSeparator(borrowerSeperator)
  }, [borrowerSeperator])

  useEffect(() => {
    setInputs(borrowerInfoValidate(borrowerSeperator, visibelFields, inputs, false, true) as any)
  }, [borrowerSeperator, visibelFields])

  const onChange = async (key: string, value: any, withBlur = false) => {
    let newInputs = cloneDeep(inputs)
    const input = newInputs[key]
    fieldValueChangedRef.current = true
    setFieldValueChanged(true)
    value = InputConvert(input, value)

    // Reset TIN verification status when entityTaxID changes
    if (key === 'entityTaxID') {
      setNeedVerifyTin(true)
      setTinValidateStatus(ValidateStatus.Nothing)
    }

    const json: Record<string, any> = {}
    if (key == 'layerCount') value = Number(value) || 0

    if (key === 'settlorCount') {
      const n = Math.max(0, Math.min(32, Math.floor(Number(value)) || 0))
      const prev = Array.isArray(borrower[borrowerSeperator].settlers) ? [...borrower[borrowerSeperator].settlers!] : []
      const next = Array.from({ length: n }, (_, i) => (i < prev.length && prev[i] != null ? String(prev[i]) : ''))
      json.settlorCount = n
      json.settlers = next
    } else if (/^settlor\d+$/.test(key)) {
      const i = Number(key.replace('settlor', ''))
      const next = [
        ...(Array.isArray(borrower[borrowerSeperator].settlers) ? borrower[borrowerSeperator].settlers! : []),
      ]
      while (next.length <= i) next.push('')
      next[i] = value
      json.settlers = next
    } else if (!isCustomFieldKey(key)) json[key] = value
    else
      json.customInformation = {
        ...borrower[borrowerSeperator].customInformation,
        [FieldOrigin.BorrowerInformation]: {
          ...(borrower[borrowerSeperator]?.customInformation?.[FieldOrigin.BorrowerInformation] || {}),
          [key]: value,
        },
      }

    if (key === 'borrowerType' && value === 'Individual') {
      json['hasEntityTitle'] = false
      withBlur = true
    }
    if (key === 'hasCoborrower') {
      await changeIncludeCoborrower(value)
    } else if (key.startsWith('coborrower') && key.endsWith('Email')) {
      const temp: Record<string, any> = {}
      const idx = Number(key.replace('coborrower', '').replace('Email', ''))
      const coBorrowerKey = Object.keys(borrower)[idx]
      temp.email = value
      await dispatch(setBorrowerGroupData(coBorrowerKey, temp))
    } else {
      await dispatch(setBorrowerGroupData(borrowerSeperator, json))
    }
    let _visibelFields = visibelFields
    if (['hasEntityTitle', 'ownedRentedYears', 'borrowerType', 'hasCoborrower', 'settlorCount'].indexOf(key) !== -1) {
      _visibelFields = visibleBorrowerFieldsLogic(borrowerSeperator)
      setVisibleFields(_visibelFields)
    }
    setInputs(borrowerInfoValidate(borrowerSeperator, _visibelFields, inputs) as any)
    if (withBlur) {
      fieldValueChangedRef.current = false
      setFieldValueChanged(false)
      props.setLoading(true)
      const reqBody = {
        ...json,
        borrowerSeperator,
      }
      if (!['hasCoborrower'].includes(key)) await saveBorrowerInfoWithConditionPrompt(trimObjectStrings(reqBody))
      props.setLoading(false)
      borrowerInfoValidate(borrowerSeperator, [], {}, true, true)
    }
  }

  const onBlur = async (key: string) => {
    const input: InputType = inputs[key]
    if (!fieldValueChangedRef.current && !fieldValueChanged && input.inputType != 'FileTable') return
    if (['hasCoborrower'].includes(key)) return
    fieldValueChangedRef.current = false
    setFieldValueChanged(false)
    // if (input.error && input.error.length > 0) return
    props.setLoading(true)
    // Prefer the store: date onChange dispatches before this blur in the same turn, but the
    // `borrower` from useSelector is still the previous render's snapshot.
    const liveBorrower = store.getState().borrower?.[borrowerSeperator] || {}
    let reqBody: Record<string, any> = {
      borrowerSeperator,
    }
    if (isCustomFieldKey(key)) {
      reqBody.customInformation = {
        [FieldOrigin.BorrowerInformation]: {
          ...(liveBorrower?.customInformation?.[FieldOrigin.BorrowerInformation] || {}),
          [key]: liveBorrower?.customInformation?.[FieldOrigin.BorrowerInformation]?.[key] ?? input.value,
        },
      }
    } else if (key === 'settlorCount') {
      reqBody.settlorCount = liveBorrower.settlorCount
    } else if (/^settlor\d+$/.test(key)) {
      reqBody.settlers = liveBorrower.settlers
    } else reqBody[key] = liveBorrower[key]

    if (key.startsWith('coborrower') && key.endsWith('Email')) {
      const idx = Number(key.replace('coborrower', '').replace('Email', ''))
      const coBorrowerKey = Object.keys(borrower)[idx]
      const liveCo = store.getState().borrower?.[coBorrowerKey]
      reqBody = {
        borrowerSeperator: coBorrowerKey,
        email: liveCo?.email ?? input.value,
      }
      borrowerInfoValidate(coBorrowerKey as any, [], {}, true, true)
    }

    await saveBorrowerInfoWithConditionPrompt(trimObjectStrings(reqBody))
    props.setLoading(false)
    borrowerInfoValidate(borrowerSeperator, [], {}, true, true)

    setVisibleFields(visibleBorrowerFieldsLogic(borrowerSeperator))
  }

  const changeIncludeCoborrower = async (value: boolean) => {
    props.setLoading(true)
    setAction('changeIncludeCoborrower')

    const res = await getBorrowerSignStatus()

    if (res.success) {
      await dispatch(setBorrowerGroupData('borrower', res.data))
      signAppValidate(BorrowerSeperator.BORROWER, true)

      let continueChange = true
      if (res.confirmMessage?.length > 0) {
        props.setLoading(false)
        const rlt = await confirm(res.confirmMessage)
        props.setLoading(true)
        if (!rlt) continueChange = false
      }

      if (continueChange) {
        await updateLoanFields({
          includeCoborrower: value,
        })
        await dispatch(setLoanDetail({ includeCoborrower: value }))
        await dispatch(
          setBorrowerGroupData('borrower', {
            signedDate: '',
            signedPdfKey: '',
            signRequestSent: false,
          }),
        )
        signAppValidate(BorrowerSeperator.BORROWER, true)
      }
    }
    setAction('')
    props.setLoading(false)
  }

  const showHistory = async (key: string) => {
    const input = inputs[key]
    const options = {
      table: 'Borrower',
      field: !isCustomFieldKey(key)
        ? `${borrowerSeperator}-${key}`
        : `${borrowerSeperator}-${FieldOrigin.BorrowerInformation}-${key}`,
      keys: {
        field: !isCustomFieldKey(key) ? key : `customInformation[${FieldOrigin.BorrowerInformation}][${key}]`,
        borrowerSeperator,
      },
      renderValue: input.options,
    }
    openAuditLog(options)
  }

  const { isLocked, resolveCanEdit } = useLockContext()
  const canEdit = resolveCanEdit(
    { type: 'section', borrowerSeperator, fieldOrigin: FieldOrigin.BorrowerInformation },
    canEditLoanApplicationForBorrower(borrowerSeperator) && isSelfBorrower,
  )

  const onVerifyTin = async () => {
    if (action) return
    setAction('verifyTin')
    setTinValidateStatus(ValidateStatus.Loading)
    const res = await verifyBorrowerTin(borrowerSeperator, entityTaxIDConvertor(inputs.entityTaxID.value))
    if (res.success) {
      setTinValidateStatus(ValidateStatus.Success)
      toast('Entity Tax ID has been verified', { type: 'success' })
    } else {
      setTinValidateStatus(ValidateStatus.Failed)
      toast('Entity Tax ID has not been verified', { type: 'warning' })
    }
    setAction('')
    setNeedVerifyTin(false)
  }

  const renderTinValidate = () => {
    switch (tinValidateStatus) {
      case ValidateStatus.Nothing:
        return <></>
      case ValidateStatus.Loading:
        return <img src={svgLoading} className="w-[14px] h-[14px] text-white animate-spin" />
      case ValidateStatus.Success:
        return <CheckIcon className="h-[14px] w-[14px] text-green-600 cursor-pointer" aria-hidden="true" />
      case ValidateStatus.Warning:
        return (
          <p className="bg-yellow-300 rounded-full h-[14px] w-[14px] text-white cursor-pointer text-center leading-3">
            !
          </p>
        )
      case ValidateStatus.Failed:
        return (
          <p className="bg-red-500 rounded-full h-[14px] w-[14px] text-white cursor-pointer text-center leading-3">x</p>
        )
    }
  }

  const validateEmail = async () => {
    // const anotherKey = borrowerSeperator == 'borrower' ? 'coBorrower' : 'borrower'
    const borrowerTitle = capitalizeFirstLetter(borrowerSeperator)
    // const otherBorrowerTitle = capitalizeFirstLetter(anotherKey)
    // const needCheckOtherBorrower = borrowerSeperator == 'coBorrower' || loanDetail.includeCoborrower

    const email = borrower[borrowerSeperator]?.email
    const error = InputValidate({ ...inputs.email, value: email })
    const messages = []
    if (!email || error) {
      messages.push(`${borrowerTitle} Email`)
    }
    // if (needCheckOtherBorrower && !borrower[anotherKey].email) {
    //   messages.push(`${otherBorrowerTitle} Email`)
    // }

    // if (needCheckOtherBorrower && borrower[anotherKey].email == email) {
    //   messages.push(`Please confirm ${otherBorrowerTitle} Email again. You can't have same email as ${borrowerTitle}`)
    // }

    if (messages.length) {
      let html = `<div style="text-align:left; padding: 12px; background-color:#fde8e8; border:1px solid #f98080; border-radius: 5px; color: #d12222; font-size: 16px;"><p style="font-weight: 600;">Please complete the following fields before sending to the borrower.<p>`
      html += messages.map((message) => `<p style="margin-left: 13px; text-indent: -13px;">- ${message}</p>`).join('')
      html += `</div>`
      await confirm(html, { titleYes: false, titleNo: 'Close' })
      return false
    }

    return email
  }

  const validateCoBorrowersEmail = async () => {
    const messages: string[] = []
    Object.keys(borrower).forEach((key, index) => {
      if (key === 'borrower') return
      const borrowerTitle = capitalizeFirstLetter(key)
      const email = borrower[key]?.email
      const input = inputs[`coborrower${index}Email`]
      const error = input ? InputValidate({ ...input, value: email }) : 'Required'
      if (!email || error) {
        messages.push(`${borrowerTitle} Email`)
      }
    })

    if (messages.length) {
      let html = `<div style="text-align:left; padding: 12px; background-color:#fde8e8; border:1px solid #f98080; border-radius: 5px; color: #d12222; font-size: 16px;"><p style="font-weight: 600;">Please complete the following fields before sending to the borrower.<p>`
      html += messages.map((message) => `<p style="margin-left: 13px; text-indent: -13px;">- ${message}</p>`).join('')
      html += `</div>`
      await confirm(html, { titleYes: false, titleNo: 'Close' })
      return false
    }

    return borrower[borrowerSeperator].email
  }

  const sendApplication = async () => {
    const email = await validateEmail()
    if (!email) return

    setAction('sendApplication')
    sendApplicationTo(email, borrowerSeperator)
      .then(() => {
        toast('Application is sent to Borrower', { type: 'success' })
      })
      .finally(() => setAction(''))
  }

  const sendApplicationToCoBorrower = async () => {
    const email = await validateCoBorrowersEmail()
    if (!email) return

    setAction('sendApplicationToCoBorrowers')
    sendApplicationToCoBorrowers()
      .then(() => {
        toast('Application is sent to CoBorrowers', { type: 'success' })
      })
      .finally(() => setAction(''))
  }

  const sendDrawRequest = async () => {
    const email = await validateEmail()
    if (!email) return

    setAction('sendDrawRequest')
    senDrawRequestTo(email, borrowerSeperator)
      .then(() => {
        toast('Draw Request is sent to Borrower', { type: 'success' })
      })
      .finally(() => setAction(''))
  }

  const copyAppURL = async () => {
    const email = await validateEmail()
    if (!email) return

    setAction('copyApplicationUrl')
    copyApplicationToUrl(email, borrowerSeperator)
      .then(({ link }) => {
        copyClipboard(link)
      })
      .finally(() => setAction(''))
  }

  const onDownloadSSAPdf = async () => {
    const validationFields = ['firstName', 'lastName', 'dob', 'ssn']
    let hasError = false
    const newInputs = cloneDeep(inputs)
    for (const key of validationFields) {
      if (visibelFields.indexOf(key) === -1) continue
      const input: any = newInputs[key]
      if (!input) continue
      input.error = InputValidate(input)
      if (input.error && input.error.length > 0) hasError = true
    }
    if (hasError) {
      setInputs(newInputs)
      toast(`First Name, Last Name, DOB, SSN is required!`, { type: 'error' })
      return
    }

    setAction('generateSSAPdf')
    const data: Blob = await getSSAPdf('borrower')
    downloadFile(
      `SSA Form - ${getLoanDisplayNo(loanDetail)}(${borrower.borrower?.firstName} ${borrower.borrower?.lastName}).pdf`,
      data,
    )
    setAction('')
  }

  const onEditResidence = async (key: string) => {
    const residenceType = key === 'presentAddress' ? ResidenceType.Current : ResidenceType.Former
    const existingResidences: IResidence[] = borrower[borrowerSeperator].residences || []
    const residenceInputs = ResidenceInputs()
    const defaultAddress: string = borrower[borrowerSeperator][key] || ''

    const fieldKeys = Object.keys(residenceInputs).filter(
      (k) => !['section', 'space'].includes(residenceInputs[k].inputType),
    )
    const emptyResidence = fieldKeys.reduce<Record<string, any>>((obj, k) => ({ ...obj, [k]: '' }), {})

    let updatedResidences: IResidence[]
    let nextAddress: string

    if (residenceType === ResidenceType.Current) {
      const existing =
        existingResidences.find((r) => r.type === ResidenceType.Current) ??
        ({ ...emptyResidence, type: ResidenceType.Current } as IResidence)

      const values = await openInputsFormDlg({
        title: 'Edit Current Residence',
        inputs: residenceInputs,
        values: { ...existing, address: existing.address || defaultAddress },
      })
      if (!values || typeof values !== 'object') return

      const updated = { ...existing, ...(values as Record<string, any>), type: ResidenceType.Current } as IResidence
      const idx = existingResidences.findIndex((r) => r.type === ResidenceType.Current)
      updatedResidences =
        idx >= 0 ? existingResidences.map((r, i) => (i === idx ? updated : r)) : [...existingResidences, updated]
      nextAddress = updated.address || ''
    } else {
      const formerRows = existingResidences.filter((r) => r.type === ResidenceType.Former)
      const values = await openInputsTableDlg({
        title: 'Edit Former Residences',
        inputs: residenceInputs,
        rows: formerRows.length
          ? formerRows
          : [{ ...emptyResidence, address: defaultAddress, type: ResidenceType.Former }],
        columnKeys: ['address', 'noYears', 'livingStatus'],
        columnHeaders: {
          noYears: 'No Years',
          livingStatus: 'Living Status',
        },
      })
      if (!values || !Array.isArray(values)) return

      const updatedFormer = (values as Record<string, any>[]).map(
        (item) => ({ ...emptyResidence, ...item, type: ResidenceType.Former } as IResidence),
      )
      updatedResidences = [...existingResidences.filter((r) => r.type !== ResidenceType.Former), ...updatedFormer]
      nextAddress = updatedFormer[0]?.address || ''
    }

    const update = { residences: updatedResidences, [key]: nextAddress }

    props.setLoading(true)
    await dispatch(setBorrowerGroupData(borrowerSeperator, update))
    await saveBorrowerInfoWithConditionPrompt(trimObjectStrings({ borrowerSeperator, ...update }))
    props.setLoading(false)
    borrowerInfoValidate(borrowerSeperator, [], {}, true, true)
    setVisibleFields(visibleBorrowerFieldsLogic(borrowerSeperator))
  }

  const renderInput = (key: string, index: number) => {
    if (visibelFields.indexOf(key) === -1 || !inputs) return null
    const input = cloneDeep(inputs[key])

    if (isCustomFieldKey(key)) {
      input.value = borrower[borrowerSeperator]?.['customInformation']?.[FieldOrigin.BorrowerInformation]?.[key]
    } else if (['settlorCount', 'layerCount'].includes(key)) {
      input.value = borrower[borrowerSeperator]?.[key] || 0
    } else if (/^settlor\d+$/.test(key)) {
      const idx = Number(key.replace('settlor', ''))
      input.value = (borrower[borrowerSeperator].settlers || [])[idx] ?? ''
      const n = Math.max(0, Math.min(32, Number(borrower[borrowerSeperator].settlorCount) || 0))
      if (n === 1) input.title = 'Settlor Name'
      else input.title = `Settlor Name #${idx + 1}`
    } else if (borrower[borrowerSeperator][key] !== undefined) {
      input.value = borrower[borrowerSeperator][key]
    } else {
      input.value = ''
    }
    if (key === 'hasCoborrower') input.value = loanDetail.includeCoborrower
    else if (key === 'numOfCoBorrowers') {
      input.value = Object.keys(borrower).length - 1
      input.readOnly = true
    } else if (key.startsWith('coborrower') && key.endsWith('Email')) {
      const idx = Number(key.replace('coborrower', '').replace('Email', ''))
      const coBorrowerKey = Object.keys(borrower)[idx]
      input.value = borrower[coBorrowerKey]?.email
    }

    if (isViewOnlyByLogic(fieldProperties[key]?.viewOnlyLogic)) input.readOnly = true
    if (!input.readOnly && isReadOnlyByEditPermission(fieldProperties[key]?.editPermissionAccountTypes))
      input.readOnly = true

    if (!profile.isBorrower) input.history = true
    if (input.disabled) {
    } else {
      input.disabled = !canEdit
    }
    let cn = `input md:col-span-1`
    if (['hasEntityTitle'].indexOf(key) !== -1 || input.inputType == 'FileTable') {
      cn = `input md:col-span-2`
    }

    if (key === 'ownedRentedYears') input.value = input.value > 1 ? 2 : input.value

    if (['presentAddress', 'formerAddress'].includes(key)) {
      input.additionalElements = canEdit ? (
        <span
          className="ml-1 flex items-center gap-1 text-shade-blue hover:underline cursor-pointer"
          onClick={() => onEditResidence(key)}
        >
          Edit
        </span>
      ) : null
    } else if (['entityAddress', 'mailingAddress'].includes(key)) {
      const fieldLocked = isLocked({
        type: 'field',
        borrowerSeperator,
        fieldOrigin: FieldOrigin.BorrowerInformation,
        fieldKey: key,
      })
      input.additionalElements =
        fieldLocked || !canEdit ? null : (
          <span
            className="ml-1 flex items-center gap-1 text-shade-blue hover:underline cursor-pointer"
            onClick={() => onChange(key, borrower[borrowerSeperator].presentAddress, true)}
          >
            Same as {fieldProperties?.presentAddress?.title || 'Present Address'}
          </span>
        )
    } else if (key === 'firstName')
      input.additionalElements = (
        <span
          className="ml-1 flex items-center gap-1 text-shade-blue hover:underline cursor-pointer"
          onClick={() => showAlternativeNames(true)}
        >
          Alternative Names
        </span>
      )
    else if (key === 'ssn')
      input.additionalElements = (
        <span
          className="ml-1 flex items-center gap-1 text-shade-blue hover:underline cursor-pointer"
          onClick={onDownloadSSAPdf}
        >
          SSA89 PDF
          <ArrowDownTrayIcon className="h-[14px] w-[14px]" aria-hidden="true" />
        </span>
      )
    else if (key === 'entityTaxID' && vendor.Middesk && canTinCheck && !inputs[key].error)
      input.additionalElements = (
        <div className="ml-1 flex items-center gap-1">
          {tinValidateStatus !== ValidateStatus.Success && needVerifyTin && (
            <span
              className={`${
                action === 'verifyTin' || !canEdit
                  ? 'text-gray-500 cursor-not-allowed'
                  : 'text-shade-blue hover:underline cursor-pointer'
              }`}
              onClick={() => canEdit && onVerifyTin()}
            >
              Verify
            </span>
          )}
          {renderTinValidate()}
          <span className="text-shade-blue hover:underline cursor-pointer" onClick={() => setShowTinHistory(true)}>
            <ShieldCheckIcon className="h-[14px] w-[14px]" aria-hidden="true" />
          </span>
        </div>
      )
    else input.additionalElements = null
    if (fieldProperties[key] && fieldProperties[key].title && !/^settlor\d+$/.test(key))
      input.title = fieldProperties[key].title || input.title
    if (fieldProperties[key] && fieldProperties[key].type) input.type = fieldProperties[key].type || input.type
    if (fieldProperties[key]) {
      const eff = getEffectiveFieldRequiredForBorrower(borrowerSeperator, fieldProperties[key])
      if (eff !== undefined) input.required = eff
    }

    return (
      <div className={cn} key={`${key}-${index}`}>
        <RenderInput
          input={input}
          Key={key}
          onChange={onChange}
          showHistory={showHistory}
          onBlur={onBlur}
          lockScope={{ type: 'field', borrowerSeperator, fieldOrigin: FieldOrigin.BorrowerInformation, fieldKey: key }}
        />
      </div>
    )
  }

  const renderAdditionalLayerInputs = () => {
    return (
      <AdditionalLayerInputs
        borrower={borrower[borrowerSeperator]}
        borrowerSeperator={borrowerSeperator}
        onUpdate={(values: Record<string, any>[]) => {
          onChange('additionalLayer', values, true)
        }}
      />
    )
  }

  const sectionCount = loanFieldSections.length + (freeSectionFields.length ? 1 : 0)

  const applyFillableExtract = async (rows: BorrowerAiField[]) => {
    const byBorrower = new Map<string, Record<string, any>>()
    rows
      .filter((row) => row.target === 'borrower' && row.borrowerSeperator)
      .forEach((row) => {
        const current = byBorrower.get(row.borrowerSeperator || '') || {}
        if (current[row.key] === undefined) current[row.key] = row.value
        byBorrower.set(row.borrowerSeperator || '', current)
      })

    for (const [separator, json] of byBorrower) {
      const payload = trimObjectStrings({ ...json, borrowerSeperator: separator })
      if (separator === borrowerSeperator) {
        await saveBorrowerInfoWithConditionPrompt(payload)
        await dispatch(setBorrowerGroupData(separator, json))
        const nextVisible = visibleBorrowerFieldsLogic(separator)
        setVisibleFields(nextVisible)
        setInputs(borrowerInfoValidate(separator, nextVisible, inputs) as any)
      } else {
        await borrowerInfoUpdate(payload, true)
        await dispatch(setBorrowerGroupData(separator, json))
      }
    }

    if ([...byBorrower.keys()].some((separator) => separator !== 'borrower')) {
      await updateLoanFields({ includeCoborrower: true })
      dispatch(setLoanDetail({ includeCoborrower: true }))
    }

    const loan: Record<string, any> = {}
    rows
      .filter((row) => row.target === 'loan')
      .forEach((row) => {
        if (loan[row.key] === undefined) loan[row.key] = row.value
      })
    if (Object.keys(loan).length) {
      await updateLoanFields(loan)
      dispatch(setLoanDetail(loan))
    }

    for (const row of rows.filter((item) => item.target === 'pricing')) {
      await updateLoanProcess({ key: row.key, value: row.value })
    }

    const grouped = (target: BorrowerAiField['target']) => {
      const records = new Map<string, Record<string, any>>()
      rows
        .filter((row) => row.target === target)
        .forEach((row) => {
          const id = row.group || row.section || row.key
          const current = records.get(id) || {}
          current[row.key] = row.value
          records.set(id, current)
        })
      return [...records.values()]
    }
    for (const data of grouped('realEstate')) await createRentalEstateInformation(data)
    for (const data of grouped('liability')) await createLoanLiabilityInformation(data)
    toast('The filled application was imported.', { type: 'success' })
  }

  const applyAiExtract = async (rows: BorrowerAiField[]) => {
    if (rows.some((row) => row.target)) {
      props.setLoading(true)
      try {
        await applyFillableExtract(rows)
      } catch {
        toast('Failed to import the filled application.', { type: 'error' })
      }
      props.setLoading(false)
      borrowerInfoValidate(borrowerSeperator, [], {}, true, true)
      return
    }
    const json: Record<string, any> = {}
    const custom: Record<string, any> = {}
    rows.forEach((row) => {
      if (row.custom || isCustomFieldKey(row.key)) custom[row.key] = row.value
      else json[row.key] = row.value
    })
    if (Object.keys(custom).length) {
      json.customInformation = {
        ...borrower[borrowerSeperator].customInformation,
        [FieldOrigin.BorrowerInformation]: {
          ...(borrower[borrowerSeperator]?.customInformation?.[FieldOrigin.BorrowerInformation] || {}),
          ...custom,
        },
      }
    }
    await dispatch(setBorrowerGroupData(borrowerSeperator, json))
    const nextVisible = visibleBorrowerFieldsLogic(borrowerSeperator)
    setVisibleFields(nextVisible)
    setInputs(borrowerInfoValidate(borrowerSeperator, nextVisible, inputs) as any)
    props.setLoading(true)
    await saveBorrowerInfoWithConditionPrompt(trimObjectStrings({ ...json, borrowerSeperator }))
    props.setLoading(false)
    borrowerInfoValidate(borrowerSeperator, [], {}, true, true)
  }

  return (
    <div>
      {!isSignApplicationLink && loanDetail.loanType !== LoanType.CORRESPONDENT && (
        <div className="flex justify-center flex-wrap gap-4 my-2">
          <Button className="" color="gray" onClick={sendApplication} loading={action === 'sendApplication'}>
            <div className="flex gap-2 items-center">
              {borrowerSeperator === 'borrower' ? 'Send Application to Borrower' : 'Send Application to CoBorrower'}
              <ArrowTrendingUpIcon className="w-4 h-4"></ArrowTrendingUpIcon>
            </div>
          </Button>

          {borrowerSeperator === 'borrower' &&
            isConstructionLoan(loan.productType) &&
            loanDetail.loanStatus == 'funded' && (
              <Button link className="" color="gray" onClick={sendDrawRequest} loading={action === 'sendDrawRequest'}>
                <div className="flex gap-2 items-center">
                  Send Draw Request to Borrower
                  <ArrowTrendingUpIcon className="w-4 h-4"></ArrowTrendingUpIcon>
                </div>
              </Button>
            )}
          <Button link className="" color="gray" onClick={copyAppURL} loading={action === 'copyApplicationUrl'}>
            <div className="flex gap-1 items-center px-3">
              Copy Application URL
              <Square2StackIcon className="w-4 h-4"></Square2StackIcon>
            </div>
          </Button>
        </div>
      )}

      {isSignApplicationLink && borrowerSeperator === 'borrower' && isSelfBorrower && profile.isBorrower && (
        <div className="flex justify-center flex-wrap gap-4 my-2">
          <Button
            className=""
            color="gray"
            onClick={sendApplicationToCoBorrower}
            loading={action === 'sendApplicationToCoBorrowers'}
          >
            <div className="flex gap-2 items-center">
              Send Application to CoBorrowers
              <ArrowTrendingUpIcon className="w-4 h-4"></ArrowTrendingUpIcon>
            </div>
          </Button>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
        {loanFieldSections.map(
          (section) =>
            section.fields.length != 0 && (
              <div key={section.id} className={`col-span-full grid gap-4 md:grid-cols-2 grid-cols-1 mb-3`}>
                {sectionCount > 1 && (
                  <div className="col-span-full">
                    <RenderInput
                      input={{ inputType: 'section', title: section.title || '' }}
                      Key={`${section.id}`}
                      onChange={() => {}}
                    />
                  </div>
                )}
                {withCompanions(section.fields).map((key, index) => renderInput(key, index))}
              </div>
            ),
        )}
        {freeSectionFields.map((key, index) => renderInput(key, index))}

        {renderAdditionalLayerInputs()}
      </div>
      <LayoutLoading show={action === 'generateSSAPdf'} />
      {isAlternativeNames && (
        <AlternativeNamesModal borrowerSeperator={borrowerSeperator} onClose={() => showAlternativeNames(false)} />
      )}
      {showTinHistory && <TinVerifyModal loanNumber={loanDetail.loanNumber} onClose={() => setShowTinHistory(false)} />}
      {props.aiExtractOpen && (
        <BorrowerAiExtractModal
          isOpen
          loanNumber={loanDetail.loanNumber}
          borrowerSeperator={borrowerSeperator}
          allowedKeys={visibelFields}
          onClose={() => props.onAiExtractClose?.()}
          onApply={applyAiExtract}
        />
      )}
    </div>
  )
}
